import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  useOrderFeedbackAnswer,
  useOrderFeedbackQuestion,
} from '@hungryroot/box-level-feedback';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { CaretLeft, X } from '@hungryroot/icons';
import { ButtonsGroup } from '@hungryroot/pho';
import { Button, Heading, IconButton } from '@hungryroot/ramen';

import { segmentAnalytics } from '@hungryroot/segment';
import { ArchingProgressBar } from '../../ArchingProgressBar/ArchingProgressBar';
import { type useDetectChanges } from '../useDetectChanges';
import styles from './PerceivedValueStage.module.css';
import sharedStyles from '../shared.module.css';

type PerceivedValueAnswers = { [q: number]: number | null };

export const useShowPerceivedValueStage = () => {
  const featureFlag = useFeatureFlag(
    'web.feature-flag.perceived_value_questions.enabled'
  );

  const { data } = useOrderFeedbackQuestion({
    type: 'value',
    enabled: Boolean(featureFlag),
  });

  const questionCount = data?.results.length ?? 0;

  return {
    shouldRender: Boolean(featureFlag && data?.results?.length),
    questionCount,
  };
};

const usePerceivedValueStage = ({
  changeDetection,
}: {
  changeDetection: ReturnType<typeof useDetectChanges>;
}) => {
  const featureFlag = useFeatureFlag(
    'web.feature-flag.perceived_value_questions.enabled'
  );

  const { data } = useOrderFeedbackQuestion({
    type: 'value',
    enabled: Boolean(featureFlag),
  });
  const { mutate: postAnswers } = useOrderFeedbackAnswer({ type: 'value' });
  const orderId = data?.results?.[0]?.order_id;
  const initialAnswersState: PerceivedValueAnswers =
    data?.results.reduce((acc, question) => {
      acc[question.id] = null;
      return acc;
    }, {} as PerceivedValueAnswers) ?? {};

  const [answers, setAnswers] =
    useState<PerceivedValueAnswers>(initialAnswersState);

  const answerCount = Object.values(answers).filter(Boolean).length;

  const answerQuestion =
    (question: keyof PerceivedValueAnswers) =>
    (value: PerceivedValueAnswers[keyof PerceivedValueAnswers]) => {
      changeDetection.updatePerceivedValue({
        question,
        prevValue: answers[question] as string | null,
        newValue: value as string | null,
      });

      const subquestion = data?.results.find((q) => q.id === question)?.name;

      const buttonClickedName = data?.results
        .find((q) => q.id === question)
        ?.answers.find((a) => a.id === value)?.name;

      segmentAnalytics.track('Perceived Value Questions', {
        order_id: orderId,
        subquestion,
        response: buttonClickedName,
      });

      setAnswers((prev) => ({ ...prev, [question]: value }));
    };

  const onSubmit = () => {
    const answerPayload = Object.entries(answers).reduce(
      (acc, [question, answer]) => {
        if (answer !== null) {
          acc.push({
            questionId: parseInt(question),
            answerId: answer as number,
          });
        }
        return acc;
      },
      [] as { questionId: number; answerId: number }[]
    );

    if (!orderId || answerPayload.length === 0) return;

    postAnswers({
      orderId,
      answers: answerPayload,
    });
  };

  return {
    answers,
    answerQuestion,
    answerCount,
    questions: data?.results ?? [],
    onSubmit,
    orderId,
  };
};

interface PerceivedValueStageProps {
  nextStage?: () => void;
  prevStage?: () => void;
  visible: boolean;
  dismissModal: () => void;
  ratedItemsState: {
    ratedItemsCount: number;
    setRatedItemsCount: (prev: (count: number) => number) => void;
  };
  totalItemsCount: number;
  changeDetection: ReturnType<typeof useDetectChanges>;
}

export function PerceivedValueStage({
  nextStage,
  prevStage,
  visible,
  dismissModal,
  totalItemsCount,
  ratedItemsState: { ratedItemsCount },
  changeDetection,
}: PerceivedValueStageProps) {
  const { answers, answerQuestion, answerCount, questions, onSubmit, orderId } =
    usePerceivedValueStage({
      changeDetection,
    });

  useEffect(() => {
    segmentAnalytics.track('Perceived Value Questions', {
      order_id: orderId,
    });
    // This should be ran once, and only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={clsx(
        sharedStyles['visibility-transition'],
        styles['perceived-value-stage']
      )}
      data-visible={visible || null}
    >
      <div className={clsx(sharedStyles.header)}>
        <IconButton
          className={clsx(sharedStyles['prev-stage-button'], {
            [sharedStyles['show-prev-stage-button']]: Boolean(prevStage),
          })}
          style="muted"
          prominence="tertiary"
          size="medium"
          icon={<CaretLeft />}
          onClick={prevStage}
          ariaLabel="Previous stage"
        />
        <ArchingProgressBar
          value={ratedItemsCount + answerCount}
          maxValue={totalItemsCount}
          aria-label="Number of questions answered"
        />
        <IconButton
          style="muted"
          prominence="tertiary"
          size="medium"
          icon={<X />}
          onClick={dismissModal}
          ariaLabel="Dismiss modal"
        />
      </div>

      <Heading variant="H1Alt" className={styles.header}>
        Any feedback on your last order?
      </Heading>
      <div className={styles.content}>
        {questions.map((question) => (
          <div className={styles.question} key={question.id}>
            <Heading variant="H5">{question.name}</Heading>
            <ButtonsGroup
              type="rounded"
              selected={answers[question.id]}
              handleClick={answerQuestion(question.id)}
              options={question.answers.map((answer) => ({
                label: answer.name,
                value: answer.id,
              }))}
              buttonClassName={styles['button-group-options']}
            />
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <Button
          className={styles['continue-button']}
          onClick={() => {
            onSubmit();
            nextStage?.();
          }}
          prominence="primary"
          style="default"
          size="medium"
          centerContent={true}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
